import axios from 'axios';

import { getAppCheckToken } from './firebase';
import {
  endSession,
  storeTokens,
  isAuthFailure,
  getAccessToken,
  getRefreshToken,
  accessTokenExpired,
} from './session';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1',
  // Cross-origin (admin.ezymind.in -> api.ezymind.in) cookies need an explicit
  // opt-in; without it the trusted-device cookie is never sent on login.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const requestRefresh = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  try {
    const { data } = await axiosInstance.post(
      '/auth/token/refresh/',
      { refresh_token: refreshToken },
      { skipSessionRefresh: true }
    );
    if (!data?.access_token || !data?.refresh_token) return false;
    storeTokens(data.access_token, data.refresh_token);
    return true;
  } catch {
    // A rejected refresh token is the end of the session, reported by the caller.
    return false;
  }
};

// One refresh at a time: a burst of expired requests shares a single call and its result.
let refreshInFlight = null;
const refreshSession = () => {
  if (!refreshInFlight) {
    refreshInFlight = requestRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

axiosInstance.interceptors.request.use(
  async (config) => {
    // Respect a pre-set Authorization (MFA enroll/verify use scoped Bearer tokens), and
    // leave the refresh call itself unauthenticated - it is AllowAny and reads the body.
    if (!config.headers.Authorization && !config.skipSessionRefresh) {
      // Renew ahead of the call so a stale token doesn't cost every request a round trip.
      if (getAccessToken() && accessTokenExpired()) {
        await refreshSession();
      }
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        // Marks the header as ours, so the response side knows it may re-stamp it on a
        // retry and that a caller-supplied scoped token (MFA) is not its business.
        config.sessionAuthorized = true;
      }
    }
    // Best-effort App Check token; null until Firebase App Check is configured.
    const appCheckToken = await getAppCheckToken();
    if (appCheckToken) {
      config.headers['X-Firebase-AppCheck'] = appCheckToken;
    }
    // Don't set Content-Type for FormData - let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Only requests this interceptor authorized: never a scoped MFA token, never the
    // refresh call itself (which would recurse).
    const ownsSession = Boolean(originalRequest?.sessionAuthorized);

    if (ownsSession && isAuthFailure(error)) {
      if (!originalRequest.retriedAfterRefresh) {
        originalRequest.retriedAfterRefresh = true;
        if (await refreshSession()) {
          // Re-stamp the fresh token. Retrying with the rejected one would fail again and
          // then tear down the session we just successfully renewed.
          originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
          return axiosInstance(originalRequest);
        }
      }
      // Refresh token is gone or rejected: drop the session so the app routes to login
      // instead of rendering a shell where every call fails.
      endSession();
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
